/**
 * IMPORTANT NOTES
 *********************************************************
 * DEMO:
 * Hosting: dcfunction018b46/$web
 * Url: https://dcfunction018b46.z6.web.core.windows.net/
 * Akamai: https://securema2.ikea.com/sa/ar/booking/
 *
 * PROD:
 * Hosting: dcbooking/$web
 * Url: https://dcbooking.z6.web.core.windows.net/
 * Akamai: https://securema.ikea.com/sa/en/booking/
 *
 */

import React, { useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { create } from "jss";
import rtl from "jss-rtl";

import {
  makeStyles,
  StylesProvider,
  jssPreset,
} from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/core/styles";
import { Container } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Button from "@material-ui/core/Button";

import { getDateRange, formatTime } from "../../utils/formatTime";
import b64EncodeUnicode from "../../utils/b64EncodeUnicode";
import isMeasurement from "../../utils/isMeasurement";

import theme from "../../theme";
import { STEPS, SERVICE_TYPE } from "../../types";

import AddressPage from "./address.page";
import ServicePage from "./service.page";
import TimeslotPage from "./timeslot.page";
import ContactPage from "./contact.page";

// API
import createOrderAsync from "./api/createOrderAsync";
import redirectToPayfortAsync from "./api/redirectToPayfortAsync";
import payfortSignatureAsync from "./api/payfortSignatureAsync";
import reserveTimeslotAsync from "./api/reserveTimeslotAsync";
import releaseTimeslotAsync from "./api/releaseTimeslotAsync";

const useStyles = makeStyles((theme) => ({
  // formControl: {},
  // selectEmpty: {},

  // layout: {
  //   width: "auto",
  //   marginLeft: theme.spacing(2),
  //   marginRight: theme.spacing(2),
  //   [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
  //     width: 600,
  //     marginLeft: "auto",
  //     marginRight: "auto",
  //   },
  // },
  // paper: {
  //   marginTop: theme.spacing(3),
  //   marginBottom: theme.spacing(3),
  //   padding: theme.spacing(2),
  //   [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
  //     marginTop: theme.spacing(6),
  //     marginBottom: theme.spacing(6),
  //     padding: theme.spacing(3),
  //   },
  // },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  paymentLogo: {
    height: "40px",
    width: "80px",
    border: "1px solid #ccc",
    textAlign: "center",
    borderRadius: "4px",
    padding: "5px",
    margin: "5px",
  },
}));

export default function IndexPage(props) {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const [errors, setErrors] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [disableNext, setDisableNext] = useState(false);
  const [fields, setFields] = useState({
    uuid: uuidv4().toUpperCase(),
    city: "",
    district: "",
    store: "",
    service: "",
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    timeslot: "",
    measurementDisclaimer: false,
  });

  let jssRtl = null;
  if (i18n.language === "ar") {
    jssRtl = rtl();
  }
  const jss = create({ plugins: [...jssPreset().plugins, jssRtl] });

  const servicesWithDisclaimer = ["1000001", "1000002"];

  // steps as translated labels to be displayed
  const steps = [t("Service"), t("Address"), t("Timeslot"), t("Contact")];

  const handleNext = () => {
    // if user in Address page and moving to timeslot
    // remove any already selected timeslot
    if (activeStep === STEPS.ADDRESS) {
      if (fields.timeslot) {
        reserveTimeslot("");

        setFields((prevState) => {
          return { ...prevState, timeslot: "" };
        });
      }
    }

    // if final step (proceed to checkout is clicked)
    if (activeStep === STEPS.CONTACT) {
      // disable next button
      setDisableNext(true);

      trackPromise(
        createOrderAsync({
          First_Name: fields.firstName,
          Last_Name: fields.lastName,
          Post_Code: isMeasurement(fields.service)
            ? fields.district.Code
            : fields.store.PostalCode,
          Mobile_No: fields.mobile,
          Email: fields.email,
          TMS_Id:
            fields.timeslot.type === SERVICE_TYPE.OMBORI
              ? fields.timeslot.positionId
              : fields.timeslot.id,
          TMS_Date: fields.timeslot.actual_date,
          Customer_Address: fields.district.Name + ", " + fields.city.Name,
          CityId: fields.city.Id,
          DistrictId: fields.district.Id,
          Serivce_ID: fields.service.ServiceId,
          Service_Type: fields.service.Service_Type,
          Agent_Code: fields.service.AgentCode,
          NAV_Item_No: fields.service.NAVItemNo,
          Order_Type: fields.service.Order_Type,
          TMS_Start_Time: fields.timeslot.start_time,
          TMS_End_Time: fields.timeslot.end_time,
          Store_No: fields.store.StoreNo,
          NAV_WSURL: fields.store.ServiceURL,
          SlotServiceType: fields.timeslot.type,
          QueueId: fields.store.QueueId,
        }).then((order) => {
          let sigParams = {
            //merchant_reference: fields.store.StoreNo + "-" + order.OrderId,
            merchant_reference: order.OrderId,
            language: i18n.language,
            amount: fields.service.Service_Fee,
            customer_name: fields.firstName + " " + fields.lastName,
            customer_email: fields.email,
            phone_number: fields.mobile,
          };

          // encoded parameters
          sigParams.order_description = b64EncodeUnicode(
            (i18n.language === "ar"
              ? fields.service.ServiceName_Ar
              : fields.service.ServiceName_En) +
              " - " +
              t(fields.service.Service_Type)
          ).replaceAll("=", "-");
          // in case of measurement, send date range
          // IMPORTANT
          // merchant_extra1 must be null for measurement
          if (isMeasurement(fields.service)) {
            const range = getDateRange(fields.timeslot.actual_date);
            const message =
              t("between") + " " + range[0] + " " + t("and") + " " + range[1];
            sigParams.merchant_extra2 = b64EncodeUnicode(message).replaceAll(
              "=",
              "-"
            );
          } else {
            // for services other than measurement send date and time
            sigParams.merchant_extra1 = b64EncodeUnicode(
              fields.timeslot.actual_date
            ).replaceAll("=", "-");

            sigParams.merchant_extra2 = b64EncodeUnicode(
              formatTime(fields.timeslot.start_time, i18n.language)
            ).replaceAll("=", "-");
          }

          // pass store only for non-online services
          if (fields.service.Service_Type !== "Online") {
            sigParams.merchant_extra3 = b64EncodeUnicode(
              i18n.language === "ar"
                ? fields.store.LocalizedName
                : fields.store.Name
            ).replaceAll("=", "-");
          }

          sigParams.merchant_extra4 = b64EncodeUnicode(
            i18n.language === "ar"
              ? // hide district if not provided
                (fields.district.LocalizedName !== undefined
                  ? fields.district.LocalizedName + "، "
                  : "") + fields.city.LocalizedName
              : (fields.district.Name !== undefined
                  ? fields.district.Name + ", "
                  : "") + fields.city.Name
          ).replaceAll("=", "-");

          trackPromise(
            // Payfort full parameters list:
            // https://docs.payfort.com/docs/api/build/index.html#authorization-purchase-request
            // some values encoded using b64EncodeUnicode() to base64 to bypass un-allowed characters by Payfort
            // since = is not allowed, it is being replaced with -

            payfortSignatureAsync(sigParams).then((params) =>
              redirectToPayfortAsync(params)
            )
          );
        })
      );
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  function getStepContent() {
    switch (activeStep) {
      case STEPS.ADDRESS:
        return (
          <AddressPage
            fields={fields}
            errors={errors}
            handleChange={handleChange}
          />
        );
      case STEPS.SERVICE:
        return (
          <ServicePage
            fields={fields}
            servicesWithDisclaimer={servicesWithDisclaimer}
            errors={errors}
            handleChange={handleChange}
          />
        );
      case STEPS.TIMESLOT:
        return (
          <TimeslotPage
            fields={fields}
            errors={errors}
            handleChange={handleChange}
          />
        );
      case STEPS.CONTACT:
        return (
          <ContactPage
            fields={fields}
            errors={errors}
            handleChange={handleChange}
          />
        );
      default:
        throw new Error("Unknown step");
    }
  }

  const disableNextButton = () => {
    switch (activeStep) {
      case STEPS.SERVICE:
        // disable is default in case no service selected
        let disable = true;

        if (servicesWithDisclaimer.includes(fields.service.ServiceId)) {
          // selected service requires disclaimer checkbox to checked
          disable = !fields.measurementDisclaimer;
        } else {
          // selected service does not require disclaimer checkbox
          // just make sure there is a service selected
          disable = !fields.service;
        }

        return disable;
      case STEPS.ADDRESS:
        if (isMeasurement(fields.service)) {
          return !(fields.city && fields.district && fields.store);
        }

        // districts are not required if not measurement
        return !(fields.city && fields.store);
      case STEPS.TIMESLOT:
        return !fields.timeslot;
      case STEPS.CONTACT:
        return (
          !(
            errors["firstName"] === "" &&
            errors["lastName"] === "" &&
            errors["email"] === "" &&
            errors["mobile"] === ""
          ) || disableNext
        );
      default:
        return true;
    }
  };

  const validate = (input, value) => {
    switch (input) {
      case "city":
        if (!value) {
          return t("City") + t(" is required");
        }
        return "";
      case "district":
        if (!value) {
          return t("District") + t(" is required");
        }
        return "";
      case "store":
        if (!value) {
          return t("Store") + t(" is required");
        }
        return "";
      case "service":
        if (!value) {
          return t("Service") + t(" is required");
        }
        return "";
      case "firstName":
        if (!value) {
          return t("First name") + t(" is required");
        }
        return "";
      case "lastName":
        if (!value) {
          return t("Last name") + t(" is required");
        }
        return "";
      case "email":
        if (!value) {
          return t("Email") + t(" is required");
        }
        if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
          return t("Email") + t(" is not correct");
        }
        return "";
      case "mobile":
        if (!value) {
          return t("Mobile") + t(" is required");
        }
        if (
          !/^(009665|9665|\+9665|05)(5|0|3|6|4|9|1|8|7)([0-9]{7})$/.test(value)
        ) {
          return t("Mobile") + t(" is not correct");
        }
        return "";
      case "timeslot":
        if (!value) {
          return t("Timeslot") + t(" is required");
        }
        return "";
      case "measurementDisclaimer":
        if (servicesWithDisclaimer.includes(fields.service.ServiceId)) {
          if (!value) {
            return t(
              "You must agree that you already had measurements by IKEA team to proceed."
            );
          }
        }
        return "";
      default:
        break;
    }
  };

  const handleChange = (input, doValidation = true) => (e) => {
    if (!e) {
      if (doValidation) {
        errors[input] = validate(input, "");
        setErrors(errors);
      }

      // release any previously reserved timeslot before storing empty timeslot in state
      if (input === "timeslot") {
        reserveTimeslot("");
      }

      setFields((prevState) => {
        return { ...prevState, [input]: "" };
      });

      return;
    }

    // dropdowns: store the e object. input: store the string value
    let value = e.constructor.name === "SyntheticEvent" ? e.target.value : e;

    // toggle measurement disclaimer checkbox value
    if (input === "measurementDisclaimer") {
      value = !fields.measurementDisclaimer;
    }

    if (doValidation) {
      errors[input] = validate(input, value);
      setErrors(errors);
    }

    // release any previously reserved timeslot before storing new timeslot in state
    if (input === "timeslot") {
      // do setFields inside reserveTimeslot
      reserveTimeslot(value);
    } else {
      setFields((prevState) => {
        return { ...prevState, [input]: value };
      });
    }
  };

  async function reserveTimeslot(ts) {
    const input = "timeslot";
    // user selected none. release any previously reserved timesolt
    // update: none selection is removed but the following is kept in case list is empty
    if (ts === "") {
      if (fields.timeslot) {
        releaseTimeslotAsync({
          slotId:
            ts.type === SERVICE_TYPE.OMBORI
              ? fields.timeslot.positionId
              : ts.id,
          queueId:
            ts.type === SERVICE_TYPE.OMBORI ? fields.store.QueueId : ts.id,
          serviceType: fields.timeslot.type,
        });

        setFields((prevState) => {
          return { ...prevState, [input]: ts };
        });
      }
    } else {
      // user selected new timeslot
      if (fields.timeslot) {
        // release old then reserve new
        releaseTimeslotAsync({
          slotId:
            ts.type === SERVICE_TYPE.OMBORI
              ? fields.timeslot.positionId
              : ts.id,
          queueId:
            ts.type === SERVICE_TYPE.OMBORI ? fields.store.QueueId : ts.id,
          serviceType: fields.timeslot.type,
        }).then(() => {
          reserveTimeslotAsync({
            slotId:
              ts.type === SERVICE_TYPE.OMBORI ? fields.store.QueueId : ts.id,
            uuid: ts.orderGUID, //fields.uuid,
            startTime: ts.slotstart,
            endTime: ts.slotend,
            serviceType: ts.type,
            slotType: fields.service.Service_Type,
          }).then((response) => {
            ts.positionId = response.PositionId;
            
            if (response.status.toLowerCase() === "success") {
              setFields((prevState) => {
                return { ...prevState, [input]: ts };
              });
            }
          });
        });
      } else {
        // just reserve, no previous timeslot to release
        reserveTimeslotAsync({
          slotId:
            ts.type === SERVICE_TYPE.OMBORI ? fields.store.QueueId : ts.id,
          uuid: ts.orderGUID, //fields.uuid,
          startTime: ts.slotstart,
          endTime: ts.slotend,
          serviceType: ts.type,
          slotType: fields.service.Service_Type,
        }).then((response) => {
          ts.positionId = response.PositionId;
          
          if (response.status.toLowerCase() === "success") {
            setFields((prevState) => {
              return { ...prevState, [input]: ts };
            });
          }
          
        });
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <StylesProvider jss={jss}>
        <Container maxWidth="sm">
          <Typography component="h1" variant="h4" align="center">
            {t("Book your appointment now")}
          </Typography>

          <Stepper
            alternativeLabel
            activeStep={activeStep}
            className={classes.stepper}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <React.Fragment>
            {activeStep === steps.length ? (
              <React.Fragment>
                <Typography variant="h5" gutterBottom>
                  Thank you for your order.
                </Typography>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {getStepContent()}
                <div className={classes.buttons}>
                  {activeStep !== 0 && (
                    <Button onClick={handleBack} className={classes.button}>
                      {t("Back")}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    className={classes.button}
                    disabled={disableNextButton()}
                  >
                    {activeStep === steps.length - 1
                      ? t("Proceed to checkout")
                      : t("Next")}
                  </Button>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
          <React.Fragment>
            <p style={{ marginBottom: "30px" }}> </p>
            <img
              src="/sa/ar/booking/images/mada.svg"
              alt={t("Credit card / mada bank Card")}
              className={classes.paymentLogo}
            />
            <img
              src="/sa/ar/booking/images/visa.svg"
              alt={t("VISA Card")}
              className={classes.paymentLogo}
            />
            <img
              src="/sa/ar/booking/images/mastercard.svg"
              alt={t("Master Card")}
              className={classes.paymentLogo}
            />
          </React.Fragment>
        </Container>
      </StylesProvider>
    </ThemeProvider>
  );
}
